import mapboxgl from "mapbox-gl";
import { findNearestAttachTarget, StageDestination } from "../../../services/stage-destinations";

// Screen radius within which a dragged stage marker ENGAGES an attachable
// item (POI/waypoint/highlight). Kept tight rather than generous — in a
// densely packed POI cluster a wide net snaps to whichever neighbour
// happens to be nearest, making it hard to pick out the icon actually under
// the cursor.
export const ATTACH_TARGET_PX = 28;

// Once engaged, the marker stays snapped until the cursor moves beyond this
// larger radius (hysteresis) — stops the snap flickering on/off at the edge.
export const ATTACH_RELEASE_PX = 40;

/**
 * Attachable item nearest to the cursor while dragging a stage-end marker.
 * Queries everything rendered around the point; the pure matcher in
 * services/stage-destinations.ts decides what counts as an item.
 */
export function findAttachTargetNear(
  map: mapboxgl.Map,
  point: { x: number; y: number },
  radiusPx: number = ATTACH_TARGET_PX
): StageDestination | null {
  let rendered: any[] = [];
  try {
    rendered = map.queryRenderedFeatures([
      [point.x - radiusPx, point.y - radiusPx],
      [point.x + radiusPx, point.y + radiusPx]
    ] as any);
  } catch (_) {
    return null;
  }
  return findNearestAttachTarget(
    rendered,
    point,
    coordinates => map.project(coordinates as any),
    radiusPx
  );
}

/**
 * Attach target under the cursor, with hysteresis: if `engaged` is still
 * within ATTACH_RELEASE_PX it keeps winning, so the snap doesn't flicker as
 * the cursor jitters near an item's edge. Otherwise the nearest item within
 * the (smaller) engage radius is returned.
 */
export function resolveAttachTarget(
  map: mapboxgl.Map,
  point: { x: number; y: number },
  engaged: StageDestination | null
): StageDestination | null {
  if (engaged) {
    const px = map.project(engaged.coordinates as any);
    if (Math.hypot(px.x - point.x, px.y - point.y) <= ATTACH_RELEASE_PX) {
      return engaged;
    }
  }
  return findAttachTargetNear(map, point, ATTACH_TARGET_PX);
}
